/**
 * Numeric field can have the following rules, which affect style and functionality (admin app):
 * - RequiredIf - set * after the label
 * - EnableIf - set both input field and label to disabled, when False
 * - ReadOnlyIf - set both input field and label to readOnly
 */

import { Component, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, ValidatorFn } from '@angular/forms';
import { selectMessagesForOpenedEditorForProperty } from 'src/app/project/store/project-data/project-data.selectors';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, map, Observable, Subscription, take } from 'rxjs';
import { TreeEntity } from '../../../core/models/entity.model';
import { EntityPropertyBaseComponent } from '../entity-property-base/entity-property-base.component';
import type { MeasurementUnit } from '../../../core/models/measurement-unit.model';
import { selectMeasurementUnitsState } from '../../../core/store/measurement-units/measurement-units.selector';
import { createNumericControlValidatorsForProperty } from '../../utils/validation-helpers';
import { selectUserLanguageCode } from '../../../user-account/store/user-settings/user-settings.selectors';

@Component({
  selector: 'app-entity-property-numeric',
  templateUrl: './entity-property-numeric.component.html',
  styleUrls: ['./entity-property-numeric.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class EntityPropertyNumericComponent extends EntityPropertyBaseComponent<number> implements OnChanges {
  messages: TreeEntity[] = [];

  messagesForOpenedEditorForProperty$ = (key: string) => this.store.select(selectMessagesForOpenedEditorForProperty(key, this.entityId));

  protected measurementUnit$: Observable<MeasurementUnit>;
  protected localeCode$: Observable<string>;

  constructor(private store: Store) {
    super();

    this.messagesForOpenedEditorForProperty$ = (propertyKey: string) =>
      this.store.select(selectMessagesForOpenedEditorForProperty(this.property.Key, this.entityId)).pipe(take(1));

    this.measurementUnit$ = this.store.select(selectMeasurementUnitsState).pipe(
      map(state => {
        return state.measurementUnits[this.property.MeasurementUnit?.Key];
      })
    );

    this.localeCode$ = this.store.select(selectUserLanguageCode);
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes['property']) {
      this.messagesForOpenedEditorForProperty$(this.property.Key)
        .pipe(take(1))
        .subscribe(messages => (this.messages = messages));
    }
  }

  getMinValue(propertyForm: UntypedFormControl) {
    return propertyForm.errors && propertyForm.errors['min'] && propertyForm.errors['min']['min'];
  }

  getMaxValue(propertyForm: UntypedFormControl) {
    return propertyForm.errors && propertyForm.errors['max'] && propertyForm.errors['max']['max'];
  }

  protected override getValidators(): ValidatorFn[] {
    return createNumericControlValidatorsForProperty(this.property);
  }

  protected override createOnChangeSubscription(): Subscription {
    return this.propertyForm.valueChanges.pipe(distinctUntilChanged()).subscribe(numberValue => {
      if (!Number.isNaN(numberValue)) {
        this.changed.emit({ propertyKey: this.propertyType.Key, value: numberValue, measurementUnit: this.property.MeasurementUnit?.Key });
      }
    });
  }
}
